import { EFontWeights } from '@appcharge/shared-ui';
import { OfferUI } from 'common/contracts';
import {
  DEFAULT_BORDER_COLOR,
  DEFAULT_BORDER_WIDTH,
  POPUP_DEFAULTS,
  PROGRESS_BAR_DEFAULTS,
  PROMOTION_DEFAULTS,
  ROLLING_OFFER_DEFAULTS,
  TRIGGERED_POPUP_DEFAULTS
} from 'views/OfferDesign/OfferDesign.consts';
import {
  GradientColor,
  OfferDesignFormValues,
  OfferDesignTypes
} from 'views/OfferDesign/OfferDesign.types';
import {
  normalizeBgColor,
  toNumberWithFallback
} from 'views/OfferDesign/OfferDesign.utils';

type FormValues = OfferDesignFormValues;

const buildBasePayload = (
  formValues: FormValues,
  offerType: OfferDesignTypes
): Partial<OfferUI> => ({
  active: true,
  offerUiType: offerType,
  name: formValues.name,
  externalId:
    formValues.externalId?.trim().replace(/\s+/g, '_') ||
    formValues.name.trim().replace(/\s+/g, '_'),
  description: 'description',
  backgroundImage: formValues.backgroundImage || '',
  horizontalBackgroundImage: formValues.horizontalBackgroundImage || ''
});

export const buildBundlePayload = (
  formValues: FormValues,
  offerType: OfferDesignTypes
): OfferUI => {
  const payload: Partial<OfferUI> = {
    ...buildBasePayload(formValues, offerType),
    buttonSuffixImage: formValues.buttonSuffixImage || '',
    buttonSuffixAnimation: formValues.buttonSuffixAnimation || '',
    borderColor: formValues.borderColor || DEFAULT_BORDER_COLOR,
    borderWidth: formValues.borderWidth ?? DEFAULT_BORDER_WIDTH
  };

  const bgColor = normalizeBgColor(formValues.backgroundColor);
  if (bgColor) {
    if (offerType === OfferDesignTypes.Bundle) {
      payload.backgroundColor = bgColor;
    } else {
      payload.specialOffer = {
        backgroundColor: bgColor
      } as OfferUI['specialOffer'];
    }
  }

  return payload as OfferUI;
};

export const buildPromotionPayload = (
  formValues: FormValues,
  offerType: OfferDesignTypes
): OfferUI => {
  const payload: Partial<OfferUI> = {
    ...buildBasePayload(formValues, offerType),
    buttonSuffixImage: formValues.buttonSuffixImage || '',
    buttonSuffixAnimation: formValues.buttonSuffixAnimation || '',
    borderColor: formValues.borderColor || DEFAULT_BORDER_COLOR,
    borderWidth: formValues.borderWidth ?? DEFAULT_BORDER_WIDTH,
    specialOffer: {
      templateType: PROMOTION_DEFAULTS.templateType,
      presentOfferEndTimer: false,
      title: formValues.title || '',
      fontSize: formValues.fontSize || PROMOTION_DEFAULTS.fontSize,
      fontWeight: (formValues.fontWeight ||
        PROMOTION_DEFAULTS.fontWeight) as EFontWeights,
      fontColor: formValues.fontColor || PROMOTION_DEFAULTS.fontColor,
      subTitle: {
        text: formValues.subTitleText || '',
        fontSize: formValues.subTitleFontSize || PROMOTION_DEFAULTS.subFontSize,
        fontWeight:
          formValues.subTitleFontWeight || PROMOTION_DEFAULTS.fontWeight,
        fontColor: formValues.subTitleFontColor || PROMOTION_DEFAULTS.fontColor
      },
      backgroundColor: normalizeBgColor(formValues.backgroundColor),
      collectText: formValues.collectText || PROMOTION_DEFAULTS.collectText
    } as OfferUI['specialOffer']
  };

  return payload as OfferUI;
};

export const buildProgressBarPayload = (
  formValues: FormValues,
  offerType: OfferDesignTypes
): OfferUI => {
  const barBgColor =
    normalizeBgColor(formValues.barBackgroundColor) ||
    PROGRESS_BAR_DEFAULTS.backgroundColor;

  const payload: Partial<OfferUI> = {
    ...buildBasePayload(formValues, offerType),
    backgroundImage: '',
    borderColor:
      normalizeBgColor(formValues.borderColor) ||
      PROGRESS_BAR_DEFAULTS.borderColor,
    borderWidth: formValues.borderWidth ?? PROGRESS_BAR_DEFAULTS.borderWidth,
    backgroundColor: barBgColor,
    progressBar: {
      backgroundOpacity: toNumberWithFallback(
        formValues.backgroundOpacity,
        PROGRESS_BAR_DEFAULTS.backgroundOpacity
      ),
      frameRadius: toNumberWithFallback(
        formValues.frameRadius,
        PROGRESS_BAR_DEFAULTS.frameRadius
      ),
      barFillColor:
        normalizeBgColor(formValues.barFillColor) ||
        PROGRESS_BAR_DEFAULTS.barFillColor,
      backgroundColor: barBgColor,
      barRadius: toNumberWithFallback(
        formValues.barRadius,
        PROGRESS_BAR_DEFAULTS.barRadius
      ),
      accumulatedItem: {
        imageUrl: formValues.accumulatedItemImageUrl || '',
        backgroundColor: PROGRESS_BAR_DEFAULTS.accumulatedItemBackgroundColor,
        backgroundImageUrl:
          formValues.accumulatedItemBackgroundImageUrl ||
          PROGRESS_BAR_DEFAULTS.accumulatedItemBackgroundImageUrl
      },
      textColor: formValues.textColor || PROGRESS_BAR_DEFAULTS.textColor,
      barBackgroundImage: formValues.barBackgroundImage || '',
      headerImage: formValues.headerImage || ''
    } as OfferUI['progressBar']
  };

  return payload as OfferUI;
};

export const buildPopupPayload = (
  formValues: FormValues,
  offerType: OfferDesignTypes
): OfferUI => {
  const payload: Partial<OfferUI> = {
    ...buildBasePayload(formValues, offerType),
    ...(formValues.badgeCoverImage
      ? { badgeCoverImage: formValues.badgeCoverImage }
      : {}),
    borderColor:
      normalizeBgColor(formValues.borderColor) || DEFAULT_BORDER_COLOR,
    borderWidth: formValues.borderWidth ?? DEFAULT_BORDER_WIDTH,
    specialOffer: {
      templateType: 'Single',
      presentOfferEndTimer: false,
      title: formValues.title || '',
      fontSize: formValues.fontSize || POPUP_DEFAULTS.fontSize,
      fontWeight: (formValues.fontWeight ||
        POPUP_DEFAULTS.fontWeight) as EFontWeights,
      fontColor: formValues.fontColor || POPUP_DEFAULTS.fontColor,
      subTitle: {
        text: '',
        fontSize: POPUP_DEFAULTS.subFontSize,
        fontWeight: POPUP_DEFAULTS.subFontWeight,
        fontColor: POPUP_DEFAULTS.fontColor
      },
      backgroundColor: normalizeBgColor(formValues.backgroundColor)
    } as OfferUI['specialOffer'],
    offerUiSubType: formValues.offerUiSubType || 'DailyBonus'
  };

  return payload as OfferUI;
};

export const buildTriggeredPopupPayload = (formValues: FormValues): OfferUI => {
  const payload: Partial<OfferUI> = {
    ...buildBasePayload(formValues, OfferDesignTypes.PopUp),
    ...(formValues.badgeCoverImage
      ? { badgeCoverImage: formValues.badgeCoverImage }
      : {}),
    specialOffer: {
      templateType: 'Single',
      presentOfferEndTimer: false,
      title: formValues.title || '',
      fontSize: formValues.fontSize || TRIGGERED_POPUP_DEFAULTS.fontSize,
      fontWeight: (formValues.fontWeight ||
        TRIGGERED_POPUP_DEFAULTS.fontWeight) as EFontWeights,
      fontColor: formValues.fontColor || TRIGGERED_POPUP_DEFAULTS.fontColor,
      backgroundColor: normalizeBgColor(formValues.backgroundColor)
    } as OfferUI['specialOffer'],
    offerUiSubType: formValues.offerUiSubType || 'PostPurchase'
  };

  return payload as OfferUI;
};

export const buildRollingOfferPayload = (
  formValues: FormValues,
  offerType: OfferDesignTypes
): OfferUI => {
  const backgroundOpacity =
    formValues.backgroundOpacity ?? ROLLING_OFFER_DEFAULTS.backgroundOpacity;
  const borderWidth =
    formValues.borderWidth ?? ROLLING_OFFER_DEFAULTS.borderWidth;

  const payload: Partial<OfferUI> = {
    ...buildBasePayload(formValues, offerType),
    borderColor:
      normalizeBgColor(formValues.borderColor) ||
      ROLLING_OFFER_DEFAULTS.borderColor,
    borderWidth,
    rollingOffer: {
      arrowColor: formValues.arrowColor || ROLLING_OFFER_DEFAULTS.arrowColor,
      arrowImage: formValues.arrowImage || '',
      headerImage: formValues.headerImage || '',
      backgroundOpacity,
      backgroundColor:
        normalizeBgColor(formValues.backgroundColor) ||
        ROLLING_OFFER_DEFAULTS.backgroundColor,
      collectBehavior: 'disappear',
      enableScroll: Boolean(formValues.enableScroll),
      scrollArrowImage: formValues.scrollArrowImage || '',
      subRollingOffer: {
        backgroundColor: (() => {
          const color: GradientColor =
            normalizeBgColor(formValues.offersBackgroundColor) ||
            ROLLING_OFFER_DEFAULTS.subOfferBackgroundColor;
          const { gradientDirection, ...rest } = color;
          return {
            ...rest,
            ...(gradientDirection ? { direction: gradientDirection } : {})
          };
        })(),
        ...(formValues.offersBackgroundImage
          ? { backgroundImage: formValues.offersBackgroundImage }
          : {}),
        lockImage: ROLLING_OFFER_DEFAULTS.lockImage,
        collectText:
          formValues.collectText || ROLLING_OFFER_DEFAULTS.collectText
      }
    } as OfferUI['rollingOffer']
  };

  return payload as OfferUI;
};
